import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type AccountInternalCreateRequest, enrolAccountInternal } from '~/services/LuxLoyalty';

import { loyaltyAccountQueryKeys } from './useGetLoyaltyAccount';

/** Argument value for {@link UseEnrolLuxLoyaltyAccountOptions.onSuccess}. */
export type EnrolledTier = App.LuxLoyaltyTier | 'default';

interface UseEnrolLuxLoyaltyAccountOptions {
  /** Called after the loyalty account query has been invalidated. */
  onSuccess?: (tier: EnrolledTier) => void;
  onError?: (error: Error) => void;
}

/**
 * Mutation hook that enrols a customer into Société via the admin-only
 * internal endpoint. Passing `tier: undefined` lets the loyalty service apply
 * its default tier-resolution logic.
 */
export function useEnrolLuxLoyaltyAccount(options: UseEnrolLuxLoyaltyAccountOptions = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: AccountInternalCreateRequest) => enrolAccountInternal(params),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: loyaltyAccountQueryKeys.byCustomer(variables.customerId),
      });
      options.onSuccess?.(variables.tier ?? 'default');
    },
    onError: options.onError,
  });
}
